import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import toast from '../../utils/toast';
import {
  User,
  Save,
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  AlertTriangle,
  Clock,
  X,
  Package
} from 'lucide-react';
import OffboardingStepSkeleton from './OffboardingStepSkeleton';
import HorizontalStepper from '../../components/UI/HorizontalStepper';
import { StatusBadge } from '../../components/UI/StatusBadge';
import WithdrawalModal from '../../components/WithdrawalModal';
import useWithdrawal from '../../hooks/useWithdrawal';
import useOffboardingWorkflow from '../../hooks/useOffboardingWorkflow';
import { staffAPI } from '../../services/api';
import FormSelect from '../../components/UI/FormSelect';
import { getOffboardingSteps, calculateProgressPercentage } from '../../config/offboardingSteps';
import { initializeStepData } from '../../utils/workflowUtils';
import { formatDate, getTodayInSingaporeTimezone, formatDateForInput, suggestEmploymentEndISO } from '../../utils/dateUtils';
import { formatLeaveDays } from '../../utils/numberUtils';

const OffboardingStep2 = () => {
  const { workflowId } = useParams();
  const navigate = useNavigate();
  const { handleWithdrawal } = useWithdrawal();
  const { workflow, loading, saving: hookSaving, saveStep } = useOffboardingWorkflow(workflowId);
  const [saving, setSaving] = useState(false);
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const getTodayDate = () => getTodayInSingaporeTimezone();

  const [stepData, setStepData] = useState({
    hod_discussion_date: getTodayDate(),
    primary_reason: '',
    departure_discussion_summary: '',
    emotional_state: '',
    leave_settlement_preference: '',
    urgent_handover_needed: false,
    successor_identified: false,
    handover_details: '',
    hod_recommendations: ''
  });


  // Handle step click navigation
  const handleStepClick = (step) => {
    navigate(step.route);
  };


  // Fetch live leave balances for the current employee
  const { data: leaveBalancesData, isLoading: leaveBalancesLoading } = useQuery({
    queryKey: ['leaveBalances', workflow?.employee?.staff_id, new Date().getFullYear()],
    queryFn: () => staffAPI.getLeaveBalances({
      year: new Date().getFullYear()
    }),
    enabled: !!workflow?.employee?.staff_id,
    select: (response) => {
      const balances = response.data?.results || response.data || [];
      // Find the Annual Leave balance for this specific employee
      const annualLeaveBalance = balances.find(
        balance => balance.staff_id === workflow?.employee?.staff_id &&
                   balance.leave_type?.leave_type_name === 'Annual Leave'
      );
      return {
        annualLeaveBalance: annualLeaveBalance?.remaining_days || 0,
        allBalances: balances
      };
    }
  });

  // Initialize step data when workflow loads
  useEffect(() => {
    if (workflow) {
      const initialData = initializeStepData(workflow, 2);
      // Override with today's date if not already set
      setStepData(prev => ({
        ...initialData,
        hod_discussion_date: initialData.hod_discussion_date || getTodayDate()
      }));
    }
  }, [workflow]);

  const handleSave = async () => {
    // Validation for required fields
    if (!stepData.leave_settlement_preference) {
      toast.error('Please select a Leave Settlement Preference');
      return;
    }

    try {
      setSaving(true);
      const expectedEnd = workflow?.resignation_date && workflow?.employee?.notice_period_months
        ? suggestEmploymentEndISO(
            workflow.resignation_date,
            workflow.employee.notice_period_months || 1,
          )
        : '';
      await saveStep(2, {
        ...stepData,
        final_working_day: stepData.final_working_day || expectedEnd,
        leave_encashment_approved: ['encashment', 'combination'].includes(stepData.leave_settlement_preference),
        leave_offset_approved: ['offset', 'combination'].includes(stepData.leave_settlement_preference),
      });
    } catch (error) {
      console.error('Error saving step:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    navigate(`/admin/employee-lifecycle/offboarding/${workflowId}/step-3`);
  };

  const handlePrev = () => {
    navigate(`/admin/employee-lifecycle/offboarding/${workflowId}/step-1`);
  };

  if (loading) {
    return <OffboardingStepSkeleton />;
  }

  if (!workflow) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Workflow Not Found</h2>
          <p className="text-gray-600">The requested offboarding workflow could not be found.</p>
        </div>
      </div>
    );
  }

  const steps = getOffboardingSteps(workflow, workflowId);
  const progressPercentage = calculateProgressPercentage(workflow);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header */}
      <div className="bg-white">
        <div className="px-6 py-8 space-y-2">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <h1 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">
                Step 2: HOD Discussion & Leave Settlement
              </h1>
              <p className="text-xs sm:text-sm text-gray-500 truncate">
                {workflow.employee.first_name} {workflow.employee.last_name} • {workflow.employee.staff_id}
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <span className="text-sm text-gray-500">Step 2 of {workflow?.requires_ir21 ? 7 : 6}</span>
              {workflow.step_2_completed && (
                <StatusBadge status="completed" showIcon />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sticky Horizontal Stepper */}
      <HorizontalStepper
        steps={steps}
        currentStep={2}
        onStepClick={handleStepClick}
        progressPercentage={progressPercentage}
      />

      {/* Main Content */}
      <div className="px-6 py-8 space-y-8">
        <div className="bg-white rounded-2xl shadow-sm border p-8">
        <div className="flex items-center mb-4 sm:mb-6 min-w-0 gap-2">
          <User className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 flex-shrink-0" />
          <h2 className="text-lg sm:text-2xl font-bold text-gray-900 min-w-0 truncate flex-1">HOD Discussion & Departure Planning</h2>
          {workflow.step_2_completed && (
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          )}
        </div>

        {/* RO Discussion Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              RO Discussion Date
            </label>
            <input
              type="date"
              value={stepData.hod_discussion_date}
              onChange={(e) => setStepData({...stepData, hod_discussion_date: e.target.value})}
              className="input-admin"
              disabled={workflow.step_2_completed}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Reporting Officer
            </label>
            <input
              type="text"
              value={workflow?.employee?.reporting_officer ?
                ((workflow.employee.reporting_officer.alias && workflow.employee.reporting_officer.alias.trim()) ?
                  `${workflow.employee.reporting_officer.alias.trim()} (${workflow.employee.reporting_officer.first_name} ${workflow.employee.reporting_officer.last_name})` :
                  `${workflow.employee.reporting_officer.first_name} ${workflow.employee.reporting_officer.last_name}`.trim()
                ) : 'Not assigned'}
              className="input-admin bg-gray-50"
              readOnly
            />
          </div>
        </div>

        {/* Departure Discussion */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <User className="w-5 h-5 text-red-600 mr-2" />
            Departure Discussion
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Primary Reason for Departure
              </label>
              <FormSelect
                options={[
                  { value: '', label: 'Select reason...' },
                  { value: 'career_change', label: 'Career Change' },
                  { value: 'better_opportunity', label: 'Better Opportunity' },
                  { value: 'relocation', label: 'Relocation' },
                  { value: 'health_reasons', label: 'Health Reasons' },
                  { value: 'retirement', label: 'Retirement' },
                  { value: 'contract_end', label: 'Contract End' },
                  { value: 'performance', label: 'Performance' },
                  { value: 'company_culture', label: 'Company Culture' },
                  { value: 'compensation', label: 'Compensation' },
                  { value: 'work_life_balance', label: 'Work-Life Balance' },
                  { value: 'other', label: 'Other' },
                ]}
                value={stepData.primary_reason}
                onChange={(val) => setStepData({...stepData, primary_reason: val})}
                theme="admin"
                disabled={workflow.step_2_completed}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Emotional State
              </label>
              <FormSelect
                options={[
                  { value: '', label: 'Select emotional state...' },
                  { value: 'positive', label: 'Positive/Excited' },
                  { value: 'neutral', label: 'Neutral' },
                  { value: 'sad', label: 'Sad/Regretful' },
                  { value: 'angry', label: 'Angry/Upset' },
                  { value: 'anxious', label: 'Anxious/Concerned' },
                  { value: 'relieved', label: 'Relieved' },
                ]}
                value={stepData.emotional_state}
                onChange={(val) => setStepData({...stepData, emotional_state: val})}
                theme="admin"
                disabled={workflow.step_2_completed}
              />
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Departure Discussion Summary
            </label>
            <input
              type="text"
              value={stepData.departure_discussion_summary}
              onChange={(e) => setStepData({...stepData, departure_discussion_summary: e.target.value})}
              className="input-admin"
              placeholder="Summarize the key points discussed during the HOD conversation..."
              disabled={workflow.step_2_completed}
            />
          </div>

        </div>

        {/* Leave Settlement */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Clock className="w-5 h-5 text-red-600 mr-2" />
            Leave Settlement Planning
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-6 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Annual Leave Balance (Days)
              </label>
              <input
                type="number"
                value={leaveBalancesLoading ? '...' : (leaveBalancesData?.annualLeaveBalance || 0)}
                className="input-admin bg-gray-50"
                readOnly
              />
              {leaveBalancesLoading && (
                <p className="text-xs text-gray-500 mt-1">Loading current balance...</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notice Period (Months)
              </label>
              <input
                type="number"
                value={workflow?.employee?.notice_period_months || 1}
                className="input-admin bg-gray-50"
                readOnly
              />
              <p className="text-xs text-gray-500 mt-1">Contract notice period requirement</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Resignation Notice Date
              </label>
              <input
                type="date"
                value={workflow?.resignation_date || ''}
                className="input-admin bg-gray-50"
                readOnly
              />
              <p className="text-xs text-gray-500 mt-1">From Step 1</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Expected Employment End Date
              </label>
              <input
                type="date"
                value={
                  workflow?.resignation_date && workflow?.employee?.notice_period_months
                    ? suggestEmploymentEndISO(
                        workflow.resignation_date,
                        workflow.employee.notice_period_months || 1,
                      )
                    : ''
                }
                className="input-admin bg-blue-50"
                readOnly
              />
              <p className="text-xs text-blue-600 mt-1">Based on notice period from resignation date</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Leave Settlement Preference <span className="text-red-500">*</span>
              </label>
              <FormSelect
                options={[
                  { value: '', label: 'Select preference...' },
                  { value: 'encashment', label: 'Full Encashment' },
                  { value: 'offset', label: 'Offset Against Notice Period' },
                  { value: 'combination', label: 'Combination' },
                  { value: 'forfeit', label: 'Forfeit Remaining Leave' },
                ]}
                value={stepData.leave_settlement_preference}
                onChange={(val) => setStepData({...stepData, leave_settlement_preference: val})}
                theme="admin"
                disabled={workflow.step_2_completed}
              />
            </div>
          </div>

          {/* Conditional Leave Settlement Details */}
          {stepData.leave_settlement_preference && (
            <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Leave Settlement Impact</h4>

              {stepData.leave_settlement_preference === 'encashment' && (
                <div className="space-y-2">
                  <div className="flex items-center text-green-700">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    <span className="text-sm font-medium">Full Encashment Selected</span>
                  </div>
                  <p className="text-xs text-gray-600 ml-6">
                    Employee will be paid out for all {leaveBalancesLoading ? '...' : (leaveBalancesData?.annualLeaveBalance || 0)} days of remaining annual leave.
                    Employment end date remains unchanged.
                  </p>
                </div>
              )}

              {stepData.leave_settlement_preference === 'offset' && (
                <div className="space-y-3">
                  <div className="flex items-center text-blue-700">
                    <Clock className="w-4 h-4 mr-2" />
                    <span className="text-sm font-medium">Offset Against Notice Period</span>
                  </div>
                  {(() => {
                    const totalLeaveDays = leaveBalancesLoading ? 0 : Number(leaveBalancesData?.annualLeaveBalance) || 0;
                    // Round down to nearest 0.5 for offset (leave can only be taken in 0.5 day increments)
                    const offsetDays = Math.floor(totalLeaveDays * 2) / 2;
                    const encashDays = totalLeaveDays - offsetDays;

                    return (
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-blue-50 p-3 rounded border">
                            <p className="text-xs font-medium text-blue-900">Leave Usage Breakdown:</p>
                            <ul className="text-xs text-blue-800 mt-1 space-y-1">
                              <li>• Total available: {totalLeaveDays} days</li>
                              <li>• Days for offset: {offsetDays} days</li>
                              <li>• Days to encash: {formatLeaveDays(encashDays)} days</li>
                            </ul>
                          </div>
                          <div className="bg-green-50 p-3 rounded border">
                            <p className="text-xs text-green-800">
                              <strong>Expected Employment End Date:</strong><br />
                              {(() => {
                                if (!workflow?.resignation_date) return 'Not available';
                                const noticeDate = new Date(workflow.resignation_date);
                                // Add notice period months, then subtract offset days
                                noticeDate.setMonth(noticeDate.getMonth() + (workflow?.employee?.notice_period_months || 1));
                                noticeDate.setDate(noticeDate.getDate() - offsetDays);
                                return formatDate(noticeDate);
                              })()}
                            </p>
                            <p className="text-xs text-green-700 mt-1">
                              Notice period shortened by {offsetDays} leave days
                            </p>
                          </div>
                        </div>
                        {encashDays > 0 && (
                          <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                            <div className="flex items-center text-yellow-800">
                              <AlertTriangle className="w-4 h-4 mr-2" />
                              <span className="text-xs font-medium">Note:</span>
                            </div>
                            <p className="text-xs text-yellow-700 mt-1">
                              {formatLeaveDays(encashDays)} days cannot be offset (must be in 0.5 day increments) and will be automatically encashed.
                              Detailed calculation will be handled in Final Settlement step.
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {stepData.leave_settlement_preference === 'combination' && (
                <div className="space-y-3">
                  <div className="flex items-center text-orange-700">
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    <span className="text-sm font-medium">Combination Approach</span>
                  </div>
                  <p className="text-xs text-gray-600">
                    Specify how to split the {leaveBalancesLoading ? '...' : (leaveBalancesData?.annualLeaveBalance || 0)} days of remaining annual leave.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Days to Encash <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        max={leaveBalancesData?.annualLeaveBalance || 0}
                        step="0.5"
                        value={stepData.encash_days || ''}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0;
                          // Validate multiples of 0.5
                          const roundedValue = Math.round(value * 2) / 2;
                          setStepData({...stepData, encash_days: roundedValue});
                        }}
                        className="input-admin"
                        placeholder="0.0"
                        disabled={workflow.step_2_completed}
                      />
                      <p className="text-xs text-gray-500 mt-1">Multiples of 0.5 days only</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Days to Offset Notice Period <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        min="0"
                        max={leaveBalancesData?.annualLeaveBalance || 0}
                        step="0.5"
                        value={stepData.offset_days || ''}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0;
                          // Validate multiples of 0.5
                          const roundedValue = Math.round(value * 2) / 2;
                          setStepData({...stepData, offset_days: roundedValue});
                        }}
                        className="input-admin"
                        placeholder="0.0"
                        disabled={workflow.step_2_completed}
                      />
                      <p className="text-xs text-gray-500 mt-1">Multiples of 0.5 days only</p>
                    </div>
                  </div>

                  {(stepData.encash_days || stepData.offset_days) && (() => {
                    const totalLeaveDays = Number(leaveBalancesData?.annualLeaveBalance) || 0;
                    const encashDays = Number(stepData.encash_days) || 0;
                    const offsetDays = Number(stepData.offset_days) || 0;
                    const totalAllocated = encashDays + offsetDays;
                    const remainingDays = totalLeaveDays - totalAllocated;

                    return (
                      <div className="space-y-2">
                        <div className={`p-3 rounded border ${totalAllocated > totalLeaveDays ? 'bg-red-50 border-red-200' : remainingDays === 0 ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
                          <p className={`text-xs font-medium ${totalAllocated > totalLeaveDays ? 'text-red-800' : remainingDays === 0 ? 'text-green-800' : 'text-blue-800'}`}>
                            Allocation Summary:
                          </p>
                          <ul className={`text-xs mt-1 space-y-1 ${totalAllocated > totalLeaveDays ? 'text-red-700' : remainingDays === 0 ? 'text-green-700' : 'text-blue-700'}`}>
                            <li>• Total allocated: {formatLeaveDays(totalAllocated)} days</li>
                            <li>• Available leave: {formatLeaveDays(totalLeaveDays)} days</li>
                            <li>• Remaining to allocate: {formatLeaveDays(Math.max(0, remainingDays))} days</li>
                          </ul>
                        </div>

                        {remainingDays > 0 && remainingDays < 0.5 && (
                          <div className="bg-yellow-50 p-3 rounded border border-yellow-200">
                            <div className="flex items-center text-yellow-800">
                              <AlertTriangle className="w-4 h-4 mr-2" />
                              <span className="text-xs font-medium">Note:</span>
                            </div>
                            <p className="text-xs text-yellow-700 mt-1">
                              {formatLeaveDays(remainingDays)} days cannot be allocated (must be in 0.5 day increments) and will be automatically encashed.
                              Detailed calculation will be handled in Final Settlement step.
                            </p>
                          </div>
                        )}

                        {totalAllocated > totalLeaveDays && (
                          <div className="bg-red-50 p-3 rounded border border-red-200">
                            <div className="flex items-center text-red-800">
                              <X className="w-4 h-4 mr-2" />
                              <span className="text-xs font-medium">Error:</span>
                            </div>
                            <p className="text-xs text-red-700 mt-1">
                              Total allocation exceeds available leave balance. Please adjust the amounts.
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {stepData.leave_settlement_preference === 'forfeit' && (
                <div className="space-y-2">
                  <div className="flex items-center text-red-700">
                    <X className="w-4 h-4 mr-2" />
                    <span className="text-sm font-medium">Forfeit Remaining Leave</span>
                  </div>
                  <p className="text-xs text-gray-600 ml-6">
                    All {leaveBalancesLoading ? '...' : (leaveBalancesData?.annualLeaveBalance || 0)} days of remaining annual leave will be forfeited.
                    Employment end date remains unchanged.
                  </p>
                </div>
              )}
            </div>
          )}

        </div>

        {/* Handover Planning */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Package className="w-5 h-5 text-red-600 mr-2" />
            Handover & Transition Planning
          </h3>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Replacement Planning <span className="text-red-500">*</span>
            </label>
            <FormSelect
              options={[
                { value: 'none', label: 'No replacement needed' },
                { value: 'normal', label: 'Replacement required (normal timeline)' },
                { value: 'immediate', label: 'Replacement required (immediate/urgent)' },
              ]}
              value={stepData.successor_identified ? (stepData.urgent_handover_needed ? 'immediate' : 'normal') : 'none'}
              onChange={(val) => {
                setStepData({
                  ...stepData,
                  successor_identified: val !== 'none',
                  urgent_handover_needed: val === 'immediate'
                });
              }}
              theme="admin"
              disabled={workflow.step_2_completed}
            />
            <p className="text-xs text-gray-500 mt-1">Select whether a replacement is needed and the urgency level for handover planning.</p>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Handover Details
            </label>
            <input
              type="text"
              value={stepData.handover_details}
              onChange={(e) => setStepData({...stepData, handover_details: e.target.value})}
              className="input-admin"
              placeholder="Describe handover requirements, key responsibilities, and timeline..."
              disabled={workflow.step_2_completed}
            />
          </div>

        </div>

        {/* HOD Recommendations */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <CheckCircle className="w-5 h-5 text-red-600 mr-2" />
            HOD Recommendations
          </h3>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Recommendations & Notes
            </label>
            <input
              type="text"
              value={stepData.hod_recommendations}
              onChange={(e) => setStepData({...stepData, hod_recommendations: e.target.value})}
              className="input-admin"
              placeholder="HOD's recommendations for the offboarding process, exit interview, knowledge transfer, etc..."
              disabled={workflow.step_2_completed}
            />
          </div>
        </div>

        {/* Navigation */}
        <div className="mt-8 flex justify-between">
          <button
            onClick={handlePrev}
            className="btn btn-admin-outline"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous Step
          </button>

          <div className="flex space-x-3">
            {!workflow.step_2_completed && workflow.workflow_status !== 'withdrawn' && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="btn btn-admin-secondary"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Progress'}
              </button>
            )}

            {!workflow.step_2_completed && workflow.workflow_status !== 'withdrawn' && (
              <button
                onClick={() => setShowWithdrawalModal(true)}
                className="btn btn-danger"
              >
                <AlertTriangle className="w-4 h-4 mr-2" />
                Withdraw
              </button>
            )}

            <button
              onClick={handleNext}
              className="btn btn-admin-primary"
            >
              Next Step
              <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>

        {/* Withdrawal Modal */}
        <WithdrawalModal
          isOpen={showWithdrawalModal}
          onClose={() => setShowWithdrawalModal(false)}
          currentStep={2}
          onConfirmWithdraw={handleWithdrawal}
          workflow={workflow}
        />
      </div>
    </div>
  );
};

export default OffboardingStep2;
